import { useMemo } from "react";
import { useList, useOne } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type { BrandKit, ContactResponse } from "pages/media/types";
import type {
  BrandKitOption,
  GeneralSettingsFormValues,
  VideoConfigOption,
} from "../types";
import { useGenerationFieldOptions } from "./useGenerationFieldOptions";

export type GeneralConfigData = {
  goals: VideoConfigOption[];
  brandKits: BrandKitOption[];
  /** Voice choices. The API field is `contact_id` — the backend derives the
   *  voice from the contact — so options are the org's contacts. */
  voices: VideoConfigOption[];
  /**
   * Org-preference defaults for the experience-wide generation fields, merged
   * under the card's static defaults so an untouched card still shows the
   * org's default brand kit / voice preselected. A partial branch: only the
   * fields the org has a stored preference for.
   */
  defaults: {
    generation_settings: Partial<
      GeneralSettingsFormValues["generation_settings"]
    >;
  };
  loading: { brandKits: boolean; voices: boolean };
  /**
   * True until the org preferences have resolved. The create card gates its
   * form on this: antd reads `initialValues` only at mount, so mounting before
   * the preference defaults exist would show empty brand kit / voice selects
   * for the whole session.
   */
  loadingDefaults: boolean;
};

/**
 * Option lists + org-preference defaults for the general card's generation
 * fields (goal, prompt, brand kit, voice). These lived in `useVideoConfigData`
 * while the fields were Video settings; they moved here with the fields when
 * the backend hoisted them to the `General` add-on type.
 */
export const useGeneralConfigData = (): GeneralConfigData => {
  const { goals } = useGenerationFieldOptions();
  const { currentOrganization, isAuthenticated } = useSession();
  const organizationId = currentOrganization?.id;

  const { data: preferencesData, isLoading: preferencesLoading } = useOne({
    resource: "media/preferences",
    id: organizationId,
    queryOptions: {
      enabled: !!organizationId && !!isAuthenticated,
      // Don't refetch for each new page; the org settings page invalidates it
      // on update.
      refetchOnMount: false,
    },
  });
  const preferences = preferencesData?.data as
    | {
        brand_kit_id?: string | null;
        contact_id?: string | null;
        generation_prompt?: string | null;
      }
    | undefined;

  const { data: brandKitData, isLoading: brandKitsLoading } = useList<BrandKit>(
    {
      resource: `media/${organizationId}/brand_kits?include_generated=false`,
      queryOptions: { enabled: !!organizationId },
    }
  );

  const { data: contactsData, isLoading: contactsLoading } =
    useList<ContactResponse>({
      resource: `media/${organizationId}/contacts`,
      queryOptions: { enabled: !!organizationId },
    });

  const brandKits = useMemo<BrandKitOption[]>(() => {
    const defaultId = preferences?.brand_kit_id;
    return (brandKitData?.data ?? []).map((kit) => ({
      value: kit.id,
      label: kit.name,
      logoUrl: kit.assets.find(
        (asset) => asset.type === "Picture" && asset.purpose === "Logo"
      )?.path,
      primaryColor: kit.palette.primary_color,
      secondaryColor: kit.palette.secondary_color ?? undefined,
      titleColor: kit.palette.title_color ?? undefined,
      titleFontUrl: kit.assets.find(
        (asset) => asset.type === "Font" && asset.purpose === "Title"
      )?.path,
      isOrgDefault: !!defaultId && kit.id === defaultId,
    }));
  }, [brandKitData, preferences?.brand_kit_id]);

  const voices = useMemo<VideoConfigOption[]>(
    () =>
      (contactsData?.data ?? []).map((contact) => ({
        value: contact.id,
        // Fall back to email (then id) so an unnamed contact stays selectable.
        label:
          `${contact.firstname} ${contact.lastname}`.trim() ||
          contact.email ||
          contact.id,
      })),
    [contactsData]
  );

  return {
    goals,
    brandKits,
    voices,
    // Coalesce the API's null (no stored preference) to undefined so unset
    // fields fall through to the Select placeholder / stay out of payloads.
    // Existence-checked (the old card's `withDefault` used to): a preference
    // pointing at a deleted kit/contact reads as "no preference" instead of
    // riding a dangling id into the display and the payload.
    defaults: useMemo<GeneralConfigData["defaults"]>(() => {
      const kits = brandKitData?.data ?? [];
      const contacts = contactsData?.data ?? [];
      return {
        generation_settings: {
          brand_kit_id: kits.some(
            (kit) => kit.id === preferences?.brand_kit_id
          )
            ? (preferences?.brand_kit_id ?? undefined)
            : undefined,
          contact_id: contacts.some(
            (contact) => contact.id === preferences?.contact_id
          )
            ? (preferences?.contact_id ?? undefined)
            : undefined,
          generation_prompt: preferences?.generation_prompt ?? undefined,
        },
      };
    }, [preferences, brandKitData?.data, contactsData?.data]),
    loading: {
      brandKits: brandKitsLoading,
      voices: contactsLoading,
    },
    // The kit/contact lists take part in resolving the defaults (existence
    // check above), so the create card's mount gate waits on all three reads.
    loadingDefaults: preferencesLoading || brandKitsLoading || contactsLoading,
  };
};

export default useGeneralConfigData;
